import type { FC, ReactNode } from 'react'
import type { Patient } from '@/lib/clinic/types'

import { useMemo, useState } from 'react'
import { cn } from '@/lib/utils'
import { useClinic } from '@/hooks/use-clinic'
import { getStats, searchPatients } from '@/lib/clinic/selectors'
import { formatPhone } from '@/lib/clinic/format'
import { formatDate } from '@/lib/clinic/dates'
import { PatientAvatar } from '@/components/app/patient-avatar'

import {
    Command,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList
} from '@/components/ui/command'

interface PatientPickerProps {
    onSelect: (patient: Patient) => void
    autoFocus?: boolean
    className?: string
}

/** Inline patient search used inside forms and dialogs. */
export const PatientPicker: FC<PatientPickerProps> = ({ onSelect, autoFocus, className }): ReactNode => {
    const { data, index } = useClinic()
    const [query, setQuery] = useState('')
    const trimmed = query.trim()

    const results = useMemo(() => {
        const list = trimmed
            ? searchPatients(data.patients, trimmed)
            : [...data.patients].sort((a, b) => (getStats(index, b.id).lastVisit ?? '').localeCompare(getStats(index, a.id).lastVisit ?? ''))

        return list.slice(0, 6)
    }, [trimmed, data.patients, index])

    return (
        <Command shouldFilter={false} loop className={cn('rounded-xl! border bg-white', className)}>
            <CommandInput
                value={query}
                onValueChange={setQuery}
                autoFocus={autoFocus}
                placeholder='Search by name, phone or patient ID'
                aria-label='Search patients'
            />

            <CommandList className='max-h-64'>
                {results.length > 0 ? (
                    <CommandGroup heading={trimmed ? 'Matching patients' : 'Recently visited'}>
                        {results.map(patient => (
                            <CommandItem key={patient.id} value={patient.id} onSelect={() => onSelect(patient)} className='gap-3 py-2'>
                                <PatientAvatar name={patient.name} seed={patient.id} size='sm' />

                                <span className='min-w-0 flex-1'>
                                    <span className='block truncate font-medium text-foreground'>
                                        {patient.name}
                                    </span>

                                    <span className='block truncate text-xs text-muted-foreground'>
                                        {patient.id} · {formatPhone(patient.phone)}
                                    </span>
                                </span>

                                <span className='hidden shrink-0 text-xs text-muted-foreground sm:block'>
                                    {formatDate(getStats(index, patient.id).lastVisit)}
                                </span>
                            </CommandItem>
                        ))}
                    </CommandGroup>
                ) : (
                    <p className='px-3 py-6 text-center text-sm text-muted-foreground'>
                        {data.patients.length === 0 ? 'No patients registered yet.' : 'No patient matches this search.'}
                    </p>
                )}
            </CommandList>
        </Command>
    )
}
